// The dropdown half of the combobox pattern: a listbox whose options are never
// focusable, driven from the input by useListNavigation.
//
// Selection is bound to mousedown with preventDefault rather than click, because
// click fires after blur — and a blur that closes the list before the click lands
// is the classic "I clicked the row and nothing happened" bug.
//
// THEMING. Two things this panel has to get right on a dark page:
//
//   * It is a floating panel over a card that is already `bg-surface`, so it cannot
//     also be `bg-surface` or it dissolves into what it covers. In light a hairline
//     plus a shadow is enough; in dark the raised surface has to be genuinely
//     lighter than the card underneath, which is what `bg-sunken` is in this palette
//     (#222b38 over #161d28). Same `dark:` exception `.field-input` already makes,
//     and for the same reason.
//   * The active row is the only cursor this screen has — SPEC.md §6 expects the
//     whole flow to be driven from the keyboard. A brand-50 wash is 1.08:1 against
//     white, which is not a cursor, it is a hint. So the highlight is a solid
//     brand-600 fill in BOTH themes: white on brand-600 is 5.1:1, and the fill reads
//     5.1:1 against the light panel and 2.8:1 against the dark one. brand-* is
//     theme-independent, so the highlight needs no `dark:` variant at all.
//
// Rows carry `group` so a caller's `renderItem` can restyle its secondary text on
// the highlighted row — text-ink-muted on a brand-600 fill would be 1.2:1.

import { useEffect, useRef } from 'react';

export default function ResultList({
  idPrefix,
  label,
  items,
  activeIndex,
  onSelect,
  onHover,
  getKey,
  renderItem,
}) {
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, items]);

  return (
    <ul
      ref={listRef}
      id={`${idPrefix}-listbox`}
      role="listbox"
      aria-label={label}
      className="mt-1 max-h-64 overflow-y-auto rounded-md border border-line bg-surface py-1 shadow-md
                 dark:border-line-strong dark:bg-sunken dark:shadow-lg dark:shadow-black/50"
    >
      {items.map((item, index) => {
        const isActive = index === activeIndex;
        return (
          <li
            key={getKey(item)}
            id={`${idPrefix}-option-${index}`}
            role="option"
            aria-selected={isActive}
            data-active={isActive}
            onMouseDown={(event) => {
              event.preventDefault();
              onSelect(item);
            }}
            onMouseMove={() => onHover?.(index)}
            className={`group cursor-pointer px-3 py-2 text-sm ${
              isActive ? 'bg-brand-600 text-white' : 'text-ink'
            }`}
          >
            {renderItem(item, isActive)}
          </li>
        );
      })}
    </ul>
  );
}
